import { Injectable } from '@angular/core';
import { catchError, map, mergeMap, startWith, switchMap } from "rxjs/operators";
import { combineLatest, forkJoin, Observable, of } from "rxjs";
import { PostRestService } from "../../../../api/services/post-rest.service";
import { ResultListApiModel } from "../../../../api/models/result-list.api.model";
import { PostApiModel } from "../../../../api/models/post-api.model";
import { MediaObjectRestService } from "../../../../api/services/media-object.rest.service";
import { MediaObjectViewModel } from "../../../../core/models/media-object.view-model";
import { QueryParamsApiModel } from "../../../../api/models/query-params-api.model";
import { MediaObjectApiModel } from "../../../../api/models/media-object.api-model";
import { FilterParameterApiModel } from "../../../../api/models/filter-parameter.api.model";
import { HistoryPostViewModel } from "./history-post.view-model";
import { PostTypeEnum } from "../../../../core/enums/post-type.enum";
import { HttpResponse } from "@angular/common/http";
import { ProjectViewModel } from "../../garage/project.view-model";
import { DescriptionPostViewModel } from "./details/description-post.view-model";
import { GoalRestService } from "../../../../api/services/goal.rest.service";
import { GoalViewModel } from "../goals/goal.view-model";
import { GoalApiModel } from "../../../../api/models/goal.api.model";
import { CostApiModel } from "../../../../api/models/cost.api.model";
import { CostViewModel } from "../goals/cost.view-model";
import { HistoryPostRestService } from "../../../../api/services/history-post-rest.service";
import { PostViewModel } from "../../../../shared/timeline/post.view-model";
import { format, addMinutes } from "date-fns";
import { DocumentRestService } from "../../../../api/services/document.rest.service";
import { DocumentApiModel } from "../../../../api/models/document.api-model";
import { DocumentUploadService } from "../../../../core/services/document-upload.service";
import { CostRestService } from "../../../../api/services/cost.rest.service";


@Injectable({
  providedIn: 'root'
})
export class AboutDataProviderService {

  constructor(
    private readonly postRS: PostRestService,
    private readonly historyPostRS: HistoryPostRestService,
    private readonly mediaObjectRS: MediaObjectRestService,
    private readonly goalRS: GoalRestService,
    private readonly costRS: CostRestService,
    private readonly documentRS: DocumentRestService,
    private readonly documentUploader: DocumentUploadService
  ) {
  }

  createDescriptionPost(postVM: DescriptionPostViewModel): Observable<DescriptionPostViewModel> {
    return this.postRS.create(AboutDataProviderService.toDescriptionApiModel(postVM)).pipe(map((postAM: PostApiModel) => {
      return AboutDataProviderService.fromDescriptionApiModel(postAM);
    }));
  }

  updateDescriptionPost(postVM: DescriptionPostViewModel): Observable<DescriptionPostViewModel> {
    return this.postRS.put(AboutDataProviderService.toDescriptionApiModel(postVM)).pipe(map((postAM: PostApiModel) => {
      return AboutDataProviderService.fromDescriptionApiModel(postAM);
    }));
  }

  createHistoryPost(postVM: HistoryPostViewModel): Observable<HistoryPostViewModel> {
    return this.postRS.create(AboutDataProviderService.toHistoryApiModel(postVM)).pipe(map((postAM: PostApiModel) => {
      return AboutDataProviderService.fromHistoryApiModel(postAM);
    }));
  }

  updateHistoryPost(postVM: HistoryPostViewModel): Observable<HistoryPostViewModel> {
    return this.postRS.put(AboutDataProviderService.toHistoryApiModel(postVM)).pipe(map((postAM: PostApiModel) => {
      return AboutDataProviderService.fromHistoryApiModel(postAM);
    }));
  }

  getDescriptionPostCollection(projectVM: ProjectViewModel): Observable<DescriptionPostViewModel[]> {
    const queryParams = new QueryParamsApiModel();
    queryParams.filters.push(new FilterParameterApiModel('discriminator', PostTypeEnum.DESCRIPTION_POST, 'array'));
    queryParams.filters.push(new FilterParameterApiModel('project', projectVM.id, 'single'));
    return this.postRS.getCollection(queryParams).pipe(mergeMap((resultListAM: ResultListApiModel<PostApiModel>) => {
      const deps = forkJoin([
        this.getMediaObjectCollection(resultListAM.records)
      ]);

      return deps.pipe(map((
        [mediaObjectMVs]: [MediaObjectViewModel[]]) => {
        return resultListAM.records.map((postAM: PostApiModel) => {
          return AboutDataProviderService.fromDescriptionApiModel(postAM);
        });
      }));
    }));
  }

  getHistoryPostCollection(projectVMs: ProjectViewModel[], queryParams: QueryParamsApiModel = new QueryParamsApiModel()): Observable<HistoryPostViewModel[]> {
    projectVMs.forEach((projectVM: ProjectViewModel) => {
      queryParams.filters.push(new FilterParameterApiModel('project', projectVM.id, 'array'));
    });
    queryParams.filters.push(new FilterParameterApiModel('order[startDate]', 'DESC', 'single'));
    queryParams.pagination = false;
    return this.historyPostRS.getCollection(queryParams).pipe(mergeMap((resultListAM: ResultListApiModel<PostApiModel>) => {
      const deps = forkJoin([
        this.getMediaObjectCollection(resultListAM.records)
      ]);

      return deps.pipe(map((
        [mediaObjectMVs]: [MediaObjectViewModel[]]) => {
        return resultListAM.records.map((postAM: PostApiModel) => {
          return AboutDataProviderService.fromHistoryApiModel(postAM);
        });
      }));
    }));
  }

  getFeaturedProjectMediaPostCollection(projectVMs: ProjectViewModel[], queryParams: QueryParamsApiModel = new QueryParamsApiModel()): Observable<PostViewModel[]> {
    projectVMs.forEach((projectVM: ProjectViewModel) => {
      queryParams.filters.push(new FilterParameterApiModel('project', projectVM.id, 'array'));
    });
    queryParams.filters.push(new FilterParameterApiModel('discriminator', PostTypeEnum.PROJECT_MEDIA_POST, 'single'));
    queryParams.filters.push(new FilterParameterApiModel('featuredAt', format(addMinutes(new Date(), 5), 'yyyy-MM-dd HH:mm:ss'), 'array_with_key', 'strictly_before'));
    queryParams.filters.push(new FilterParameterApiModel('order[featuredAt]', 'DESC', 'single'));
    queryParams.filters.push(new FilterParameterApiModel('order[createdAt]', 'DESC', 'single'));

    queryParams.pagination = false;
    return this.postRS.getCollection(queryParams).pipe(mergeMap((resultListAM: ResultListApiModel<PostApiModel>) => {
      const deps = forkJoin([
        this.getMediaObjectCollection(resultListAM.records)
      ]);

      return deps.pipe(map((
        [mediaObjectMVs]: [MediaObjectViewModel[]]) => {
        return resultListAM.records.map((postAM: PostApiModel) => {
          return new PostViewModel(postAM);
        });
      }));
    }));
  }

  deleteHistoryPost(postVM: HistoryPostViewModel): Observable<HttpResponse<any>> {
    return this.postRS.delete(AboutDataProviderService.toHistoryApiModel(postVM))
  }

  deleteGoal(goalVM: GoalViewModel): Observable<HttpResponse<any>> {
    return this.goalRS.delete(GoalApiModel.fromGoalVM(goalVM))
  }

  deleteCost(costVM: CostViewModel): Observable<HttpResponse<any>> {
    return this.costRS.delete(CostApiModel.fromCostVM(costVM));
  }

  public getMediaObjectCollection(postAMs: PostApiModel[]): Observable<MediaObjectViewModel[]> {
    // const params = new QueryParamsApiModel()
    // params.filters = postAMs
    //   .filter((e: PostApiModel) => typeof e.postedBy.avatar === 'string')
    //   .map(e => new FilterParameterApiModel('id', e.postedBy.avatar))
    // if (params.filters.length) {
    //   return this.mediaObjectRS.getMediaObjectCollection(params)
    //     .pipe(map((resultListAM: ResultListApiModel<MediaObjectApiModel>) => {
    //       return resultListAM.records.map((mediaObjectAM: MediaObjectApiModel) => new MediaObjectViewModel(mediaObjectAM))
    //     }))
    // }

    return of<MediaObjectViewModel[]>([]);
  }

  getGoalsCollection(projectVM: ProjectViewModel): Observable<GoalViewModel[]> {
    return this.goalRS.getSubResourceCollection(projectVM['@id']).pipe(map((resultListAM: ResultListApiModel<GoalApiModel>) => {
      return resultListAM.records.map((goalAM: GoalApiModel) => GoalApiModel.toGoalVM(goalAM));
    }));
  }

  getCostsCollection(projectVM: ProjectViewModel): Observable<CostViewModel[]> {
    return this.costRS.getSubResourceCollection(projectVM['@id']).pipe(map((resultListAM: ResultListApiModel<CostApiModel>) => {
      return resultListAM.records.map((costAM: CostApiModel) => CostApiModel.toCostVM(costAM));
    }));
  }

  getDocumentsCollection(projectVM: ProjectViewModel): Observable<DocumentApiModel[]> {
    if (projectVM?.canReadDocuments !== true || !projectVM?.['@id']) {
      return of([]);
    }
    return this.documentRS.getSubresourceCollection(projectVM['@id']).pipe(switchMap(result => {
      if (!result.records.length) {
        return of([]);
      }
      return combineLatest(result.records.map(document => this.documentUploader.loadReadyDocument(document).pipe(
        catchError(error => of(Object.assign(new DocumentApiModel(), document, {previewErrorKey: this.documentUploader.previewErrorKey(error)}))),
        startWith(document)
      )));
    }));
  }

  public static fromHistoryApiModel(apiModel: PostApiModel): HistoryPostViewModel {
    return {
      apiModel: apiModel,
      id: apiModel.id,
      type: apiModel.type,
      heading: apiModel.heading,
      content: apiModel.content,
      entryType: apiModel.entryType,
      project: typeof apiModel.project === 'string' ? apiModel.project : apiModel.project?.['@id'],
      collaboration: typeof apiModel.collaboration === 'string' ? apiModel.collaboration : apiModel.collaboration?.['@id'],
      createdAt: apiModel.createdAt,
      updatedAt: apiModel.updatedAt,
      startDate: apiModel.startDate,
      endDate: apiModel.endDate,
      readonly: apiModel.readonly,
    };
  }

  public static toHistoryApiModel(viewModel: HistoryPostViewModel): PostApiModel {
    const apiModel = viewModel.apiModel;
    apiModel.postedBy = null;
    apiModel.content = viewModel.content;
    apiModel.type = viewModel.type;
    apiModel.startDate = viewModel.startDate;
    apiModel.endDate = viewModel.endDate;
    apiModel.type = viewModel.type;
    apiModel.heading = viewModel.heading;
    apiModel.project = viewModel.project;
    return apiModel;
  }

  public static fromDescriptionApiModel(apiModel: PostApiModel): DescriptionPostViewModel {
    return {
      apiModel: apiModel,
      id: apiModel.id,
      type: apiModel.type,
      content: apiModel.content,
      project: typeof apiModel.project === 'string' ? apiModel.project : apiModel.project?.['@id'],
      createdAt: apiModel.createdAt,
      updatedAt: apiModel.updatedAt,
    };
  }

  public static toDescriptionApiModel(viewModel: DescriptionPostViewModel): PostApiModel {
    const apiModel = viewModel.apiModel;
    apiModel.postedBy = null;
    apiModel.content = viewModel.content;
    apiModel.type = viewModel.type;
    apiModel.project = viewModel.project;
    return apiModel;
  }
}
